import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import { translateText, getLangInfo } from '../../../lib/translate';

interface Message {
  id: string;
  client_id: string;
  subject: string;
  body: string;
  sender: string;
  original_language: string;
  is_read: boolean;
  created_at: string;
}

interface Props {
  clientId: string;
  preferredLanguage: string;
  onUnreadCountChange?: (count: number) => void;
}

export default function ClientMessages({ clientId, preferredLanguage, onUnreadCountChange }: Props) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [translatedBodies, setTranslatedBodies] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [translating, setTranslating] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [replySuccess, setReplySuccess] = useState(false);

  const langInfo = getLangInfo(preferredLanguage);

  const loadMessages = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const { data } = await supabase
        .from('client_messages')
        .select('*')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false });
      setMessages(data || []);
    } catch (err) {
      console.error('ClientMessages load error:', err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    const adminMessages = messages.filter((m) => m.sender === 'admin');
    if (adminMessages.length === 0 || preferredLanguage === 'en') {
      setTranslatedBodies({});
      return;
    }
    let cancelled = false;
    const run = async () => {
      setTranslating(true);
      const entries = await Promise.all(
        adminMessages.map(async (m) => {
          const translated = await translateText(
            m.body,
            preferredLanguage,
            m.original_language || 'en',
          );
          return [m.id, translated] as [string, string];
        }),
      );
      if (!cancelled) {
        setTranslatedBodies(Object.fromEntries(entries));
        setTranslating(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [messages, preferredLanguage]);

  const handleMarkRead = async (id: string) => {
    await supabase.from('client_messages').update({ is_read: true }).eq('id', id);
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, is_read: true } : m)));
  };

  const handleSelect = (id: string) => {
    setSelectedId((prev) => (prev === id ? null : id));
    const msg = messages.find((m) => m.id === id);
    if (msg && !msg.is_read && msg.sender === 'admin') handleMarkRead(id);
    setReplyText('');
    setReplySuccess(false);
  };

  const handleSendReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyText.trim() || !selectedId) return;
    setSendingReply(true);
    try {
      const parentMsg = messages.find((m) => m.id === selectedId);
      const subject = parentMsg ? `Re: ${parentMsg.subject}` : 'Client Reply';
      const { error } = await supabase.from('client_messages').insert({
        client_id: clientId,
        subject,
        body: replyText.trim(),
        sender: 'client',
        original_language: preferredLanguage,
        is_read: false,
      });
      if (error) throw error;
      setReplyText('');
      setReplySuccess(true);
      setTimeout(() => setReplySuccess(false), 4000);
      await loadMessages();
    } catch (err) {
      console.error('Reply send error:', err);
    } finally {
      setSendingReply(false);
    }
  };

  const adminMessages = messages.filter((m) => m.sender === 'admin');
  const unreadCount = adminMessages.filter((m) => !m.is_read).length;

  useEffect(() => {
    if (onUnreadCountChange) {
      onUnreadCountChange(unreadCount);
    }
  }, [unreadCount, onUnreadCountChange]);

  return (
    <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-8">
      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center justify-between mb-6 cursor-pointer"
      >
        <h3 className="text-2xl font-black text-white flex items-center gap-3">
          <i className="ri-message-2-line text-[#FDB913]"></i>
          Messages from Attorney
          {unreadCount > 0 && (
            <span className="bg-[#FDB913] text-black text-xs font-black rounded-full px-2.5 py-0.5 flex items-center gap-1">
              <i className="ri-mail-unread-line"></i>
              {unreadCount} new
            </span>
          )}
        </h3>
        <i className={`ri-arrow-${expanded ? 'up' : 'down'}-s-line text-white/40 text-xl`}></i>
      </button>

      {expanded && (
        <>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-[#FDB913] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : adminMessages.length === 0 ? (
            <div className="text-center py-12">
              <i className="ri-mail-open-line text-white/20 text-5xl mb-4"></i>
              <p className="text-white/40 text-base font-bold">No messages yet</p>
              <p className="text-white/25 text-sm mt-1">
                Direct messages from your attorney will appear here
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {translating && preferredLanguage !== 'en' && (
                <div className="flex items-center gap-2 bg-[#FDB913]/10 border border-[#FDB913]/20 rounded-lg px-4 py-3">
                  <div className="w-4 h-4 border-2 border-[#FDB913] border-t-transparent rounded-full animate-spin flex-shrink-0"></div>
                  <p className="text-[#FDB913] text-xs font-bold">
                    Translating to {langInfo.flag} {langInfo.label}...
                  </p>
                </div>
              )}

              {adminMessages.map((msg) => {
                const displayBody =
                  preferredLanguage !== 'en' && translatedBodies[msg.id]
                    ? translatedBodies[msg.id]
                    : msg.body;
                const isOpen = selectedId === msg.id;

                return (
                  <div
                    key={msg.id}
                    className={`border rounded-xl transition-all ${
                      isOpen
                        ? 'border-[#FDB913]/50 bg-[#FDB913]/5'
                        : msg.is_read
                          ? 'border-white/10 bg-white/5 hover:border-white/20'
                          : 'border-[#FDB913]/30 bg-[#FDB913]/5'
                    }`}
                  >
                    <button
                      onClick={() => handleSelect(msg.id)}
                      className="w-full flex items-center gap-3 p-4 cursor-pointer text-left"
                    >
                      <div
                        className={`w-9 h-9 flex items-center justify-center rounded-full flex-shrink-0 ${msg.is_read ? 'bg-white/5' : 'bg-[#FDB913]/20'}`}
                      >
                        <i
                          className={`${msg.is_read ? 'ri-mail-open-line text-white/40' : 'ri-mail-unread-line text-[#FDB913]'} text-base`}
                        ></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {!msg.is_read && (
                            <span className="w-2 h-2 bg-[#FDB913] rounded-full flex-shrink-0"></span>
                          )}
                          <p
                            className={`text-sm font-bold truncate ${msg.is_read ? 'text-white/70' : 'text-white'}`}
                          >
                            {msg.subject || 'Message from Attorney'}
                          </p>
                        </div>
                        <p className="text-white/35 text-xs mt-0.5">
                          {new Date(msg.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </p>
                      </div>
                      <i
                        className={`ri-arrow-${isOpen ? 'up' : 'down'}-s-line text-white/30 text-sm flex-shrink-0`}
                      ></i>
                    </button>

                    {isOpen && (
                      <div className="px-4 pb-5">
                        <div className="bg-black/20 border border-white/10 rounded-xl p-5 mb-4">
                          {preferredLanguage !== 'en' && translatedBodies[msg.id] && (
                            <p className="text-white/30 text-xs mb-3 flex items-center gap-1">
                              <i className="ri-translate-2 text-[#FDB913]"></i>
                              Translated to {langInfo.flag} {langInfo.label}
                            </p>
                          )}
                          <p className="text-white/85 text-sm leading-relaxed whitespace-pre-wrap">
                            {displayBody}
                          </p>
                          <p className="text-white/30 text-xs mt-4">
                            Serenity Law —{' '}
                            {new Date(msg.created_at).toLocaleString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        </div>

                        {replySuccess && (
                          <div className="flex items-center gap-2 bg-green-500/10 border border-green-500/20 rounded-lg px-4 py-2 mb-3 text-green-400 text-sm">
                            <i className="ri-checkbox-circle-line"></i>
                            Reply sent — your attorney will receive it in English.
                          </div>
                        )}

                        <form onSubmit={handleSendReply}>
                          <label className="block text-xs font-bold text-white/40 uppercase tracking-wider mb-2">
                            Reply
                          </label>
                          <textarea
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value.slice(0, 500))}
                            rows={3}
                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/25 focus:outline-none focus:border-[#FDB913] text-sm resize-none transition-colors"
                            placeholder={
                              preferredLanguage !== 'en'
                                ? `Write in ${langInfo.label} — your attorney sees it in English`
                                : 'Type your reply here...'
                            }
                          />
                          <div className="flex items-center justify-between mt-1 mb-3">
                            <p className="text-white/20 text-xs">
                              {preferredLanguage !== 'en'
                                ? `Your reply will be auto-translated to English for your attorney`
                                : ''}
                            </p>
                            <p className="text-white/25 text-xs">{replyText.length}/500</p>
                          </div>
                          <button
                            type="submit"
                            disabled={sendingReply || !replyText.trim()}
                            className="flex items-center gap-2 bg-[#FDB913]/20 hover:bg-[#FDB913]/30 border border-[#FDB913]/40 text-[#FDB913] font-bold text-sm px-4 py-2 rounded-lg transition-all disabled:opacity-50 whitespace-nowrap cursor-pointer"
                          >
                            {sendingReply ? (
                              <i className="ri-loader-4-line animate-spin"></i>
                            ) : (
                              <i className="ri-send-plane-line"></i>
                            )}
                            Send Reply
                          </button>
                        </form>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
